/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  Reducer for location module
 */

import { EMPTY_BOUNDS } from '@zcode/nr/es/shared/bizos/location'
import {
  falseReducer,
  nullReducer,
  payloadKeyReducer,
  payloadReducer,
  trueReducer,
} from '@zcode/reducers'
import type { CurrentSearch } from '@zcode/state-type'
import { combineReducers } from 'redux'
import { type ExtractActions, type Values, handleAction, handleActions } from 'redux-actions'

import type * as actions from './actions'
import {
  BOX_CHANGED,
  CENTER_CHANGED,
  CITY_NAME_CHANGED,
  CLEAR_CITY_NAME,
  CLEAR_DESTINATION_NAME,
  CLEAR_HOOD_NAME,
  CLEAR_STATE_NAME,
  COUNTRY_CODE_CHANGED,
  DESTINATION_NAME_CHANGED,
  DONE_LOADING,
  HOOD_NAME_CHANGED,
  MISSING_LOCATION,
  NOT_MISSING_LOCATION,
  START_LOADING,
  STATE_NAME_CHANGED,
  URL_CHANGED,
  ZOOM_CHANGED,
} from './constants'

export type Actions = ExtractActions<Values<typeof actions>>
type CurrentSearchLocation = CurrentSearch['location']

export const locationReducer = combineReducers<CurrentSearchLocation, Actions>({
  box: handleAction<CurrentSearchLocation['box'], Actions, typeof BOX_CHANGED>(
    BOX_CHANGED,
    payloadReducer,
    EMPTY_BOUNDS
  ),
  country: handleAction<CurrentSearchLocation['country'], Actions, typeof COUNTRY_CODE_CHANGED>(
    COUNTRY_CODE_CHANGED,
    payloadReducer,
    null
  ),
  lat: handleAction<CurrentSearchLocation['lat'], Actions, typeof CENTER_CHANGED>(
    CENTER_CHANGED,
    payloadKeyReducer('lat'),
    null
  ),
  lng: handleAction<CurrentSearchLocation['lng'], Actions, typeof CENTER_CHANGED>(
    CENTER_CHANGED,
    payloadKeyReducer('lng'),
    null
  ),
  loading: handleActions<CurrentSearchLocation['loading'], Actions>(
    {
      [START_LOADING]: trueReducer,
      [DONE_LOADING]: falseReducer,
    },
    false
  ),
  missingLocation: handleActions<CurrentSearchLocation['missingLocation'], Actions>(
    {
      [BOX_CHANGED]: falseReducer,
      [CENTER_CHANGED]: falseReducer,
      [MISSING_LOCATION]: trueReducer,
      [NOT_MISSING_LOCATION]: falseReducer,
    },
    false
  ),
  cityName: handleActions<CurrentSearchLocation['cityName'], Actions>(
    {
      [CITY_NAME_CHANGED]: payloadReducer,
      [CLEAR_CITY_NAME]: nullReducer,
    },
    null
  ),
  destinationName: handleActions<CurrentSearchLocation['destinationName'], Actions>(
    {
      [DESTINATION_NAME_CHANGED]: payloadReducer,
      [CLEAR_DESTINATION_NAME]: nullReducer,
    },
    null
  ),
  hoodName: handleActions<CurrentSearchLocation['hoodName'], Actions>(
    {
      [HOOD_NAME_CHANGED]: payloadReducer,
      [CLEAR_HOOD_NAME]: nullReducer,
    },
    null
  ),
  stateName: handleActions<CurrentSearchLocation['stateName'], Actions>(
    {
      [STATE_NAME_CHANGED]: payloadReducer,
      [CLEAR_STATE_NAME]: nullReducer,
    },
    null
  ),
  url: handleAction<CurrentSearchLocation['url'], Actions, typeof URL_CHANGED>(
    // TODO: wrap url in ensureRootRelative function to avoid errors
    URL_CHANGED,
    payloadReducer,
    null
  ),
  zoom: handleAction<CurrentSearchLocation['zoom'], Actions, typeof ZOOM_CHANGED>(
    ZOOM_CHANGED,
    payloadReducer,
    null
  ),
})
